import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

/**
 * Keeps every OTSubscriber mounted at one fixed place in the React tree while
 * the layout moves its tile around (grid -> carousel -> main area when a screen
 * share starts or stops, small overlay, whiteboard, ...).
 *
 * Rendering an OTSubscriber directly inside each layout branch means React
 * unmounts it and mounts a new one whenever the tile changes branch. Unmounting
 * removes the OpenTok widget - and the media element playing the participant's
 * voice - from the document, and the replacement has to negotiate a brand new
 * subscription, so every layout switch was heard as the voice cutting out.
 *
 * Instead each stream gets a persistent host element. The OTSubscriber is
 * portalled into that host from a stable position, and the layout only renders
 * a <SubscriberSlot>, which moves the host element into itself. React removes
 * the old slot and mounts the new one in the same synchronous commit, so the
 * media element is back in the document before the browser would pause it and
 * the audio subscription is never torn down.
 */
export class SubscriberHosts {
    constructor() {
        this.hosts = new Map();
    }

    get(streamId) {
        let host = this.hosts.get(streamId);
        if (!host) {
            host = document.createElement('div');
            host.className = 'subscriber-host';
            host.style.width = '100%';
            host.style.height = '100%';
            this.hosts.set(streamId, host);
        }
        return host;
    }

    // Wraps the subscriber in a portal into the stream's host. Call it from a
    // part of render() that is always rendered, never from a layout branch.
    portal(streamId, subscriber) {
        return ReactDOM.createPortal(subscriber, this.get(streamId), streamId);
    }

    // Drop hosts for streams that have left the session.
    prune(activeStreamIds) {
        this.hosts.forEach((host, streamId) => {
            if (!activeStreamIds.has(streamId)) {
                if (host.parentNode) {
                    host.parentNode.removeChild(host);
                }
                this.hosts.delete(streamId);
            }
        });
    }

    clear() {
        this.hosts.forEach((host) => {
            if (host.parentNode) {
                host.parentNode.removeChild(host);
            }
        });
        this.hosts.clear();
    }
}

/**
 * Placeholder rendered by the layout where a stream's video should appear.
 */
export class SubscriberSlot extends Component {
    static propTypes = {
        host: PropTypes.object.isRequired,
        className: PropTypes.string,
        style: PropTypes.object,
    };

    componentDidMount() {
        this.attachHost();
    }

    componentDidUpdate() {
        this.attachHost();
    }

    attachHost() {
        const { host } = this.props;
        // Only move the host when it is not already here - re-appending it on
        // every render would needlessly detach and reattach the video.
        if (this.node && host && host.parentNode !== this.node) {
            this.node.appendChild(host);
        }
    }

    render() {
        return (
            <div
                ref={(node) => (this.node = node)}
                className={this.props.className}
                style={this.props.style || { width: '100%', height: '100%' }}
            />
        );
    }
}
